import React from 'react';
import { Trophy } from 'lucide-react';
import { LeaderboardEntry, PublicLeaderboardResponse } from '../../application/services/LeaderboardService';
import LocalizationService from '../../application/services/LocalizationService';
import './LeaderboardBoard.css';

const t = (key: string, options?: Record<string, unknown>) =>
  LocalizationService.translate(`leaderboard.${key}`, undefined, options) as string;

/**
 * "The Passport Wall" — the leaderboard signature, extending MyCommunityPage's wax-seal/perforated
 * passport motif from a single dashboard into a shared, public wall of stamps: the top 3 get a
 * medallion podium (gold/silver/bronze wax seals, mirroring the community-score stamp), everyone
 * else is a stamped row card with a dashed "ticket" edge. Pages stay thin (App.tsx routing, param
 * resolution) — this component owns the actual board rendering, reused by all three scopes.
 */

const AVATAR_PALETTE = ['#6366F1', '#8B5CF6', '#D97706', '#0EA5E9', '#DB2777', '#059669'];

function avatarColor(userId: number): string {
  return AVATAR_PALETTE[userId % AVATAR_PALETTE.length];
}

function initial(name: string | null): string {
  return (name || '?').trim().charAt(0).toUpperCase() || '?';
}

const Avatar: React.FC<{ entry: LeaderboardEntry; size: number }> = ({ entry, size }) => {
  const style = { width: size, height: size, fontSize: size * 0.42 };
  if (entry.avatar_url) {
    return <img src={entry.avatar_url} alt="" className="lb-avatar-img" style={style} />;
  }
  return (
    <div className="lb-avatar-fallback" style={{ ...style, background: avatarColor(entry.user_id) }}>
      {initial(entry.display_name)}
    </div>
  );
};

const BadgeStrip: React.FC<{ badges: LeaderboardEntry['badges'] }> = ({ badges }) => {
  if (badges.length === 0) {
    return null;
  }
  return (
    <div className="lb-badge-strip">
      {badges.map((badge) => (
        <span key={badge.code} className="lb-badge-icon" title={badge.code} aria-label={badge.code}>
          {badge.icon}
        </span>
      ))}
    </div>
  );
};

const PodiumSeal: React.FC<{ entry: LeaderboardEntry; place: 1 | 2 | 3 }> = ({ entry, place }) => (
  <div className={`lb-podium-card lb-podium-${place}`}>
    <div className="lb-seal">
      <Avatar entry={entry} size={place === 1 ? 76 : 60} />
      <span className="lb-seal-rank">{place}</span>
    </div>
    <span className="lb-podium-name">{entry.display_name || `Member #${entry.user_id}`}</span>
    <span className="lb-podium-score">{entry.score}</span>
    <BadgeStrip badges={entry.badges} />
  </div>
);

const Row: React.FC<{ entry: LeaderboardEntry }> = ({ entry }) => (
  <li className="lb-row">
    <span className="lb-row-rank">{entry.rank}</span>
    <Avatar entry={entry} size={40} />
    <div className="lb-row-main">
      <span className="lb-row-name">{entry.display_name || `Member #${entry.user_id}`}</span>
      <span className="lb-row-substats">
        {entry.activated_referrals} {t('activatedShort')} &middot; {entry.verified_attendees} {t('verifiedShort')}
      </span>
    </div>
    <BadgeStrip badges={entry.badges} />
    <span className="lb-row-score">{entry.score}</span>
  </li>
);

interface LeaderboardBoardProps {
  board: PublicLeaderboardResponse;
  page: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
}

const LeaderboardBoard: React.FC<LeaderboardBoardProps> = ({ board, page, onPageChange, pageSize = 25 }) => {
  const isFirstPage = page === 0;
  const [podium, rest] = isFirstPage
    ? [board.entries.slice(0, 3), board.entries.slice(3)]
    : [[], board.entries];
  const totalPages = Math.max(1, Math.ceil(board.total_elements / pageSize));

  if (board.entries.length === 0) {
    return (
      <div className="lb-empty">
        <Trophy size={28} strokeWidth={1.5} />
        <p>{t('emptyTitle')}</p>
        <p className="lb-empty-hint">{t('emptyHint')}</p>
      </div>
    );
  }

  return (
    <div className="lb-board">
      {podium.length > 0 && (
        <div className="lb-podium">
          {podium[1] && <PodiumSeal entry={podium[1]} place={2} />}
          {podium[0] && <PodiumSeal entry={podium[0]} place={1} />}
          {podium[2] && <PodiumSeal entry={podium[2]} place={3} />}
        </div>
      )}

      {rest.length > 0 && (
        <ul className="lb-list">
          {rest.map((entry) => (
            <Row key={entry.user_id} entry={entry} />
          ))}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="lb-pagination">
          <button disabled={page === 0} onClick={() => onPageChange(page - 1)} className="lb-page-btn">
            {t('previous')}
          </button>
          <span className="lb-page-indicator">{t('pageIndicator', { page: page + 1, total: totalPages })}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => onPageChange(page + 1)} className="lb-page-btn">
            {t('next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default LeaderboardBoard;
